import React, { useState, useEffect, useRef } from 'react';

const ScrollingGallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const galleryRef = useRef(null);

  const images = [
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/jddohbtm_1518.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/8j8ctl91_1236.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/dqub3pnu_399.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/urvsqdio_1290.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/zyd85aum_1283.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/46vdksen_1212.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/qf5lybfv_1210.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/z13eanoa_1074.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/bj1pxv50_1623-scaled.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/wvr9ibuc_471.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/qja3s9tv_1261.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/xrgbocrb_1698-scaled.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/089uhrfy_1873-scaled.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/qr9u60y9_1752-scaled.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/s7e5letr_ISU-48-scaled.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/a1xepmxr_Orlando_2.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/z5z9dpj9_1607-scaled.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/hwkh0y2b_168.jpg",
    "https://customer-assets.emergentagent.com/job_sleek-landing-94/artifacts/j66xxxy0_163.jpg"
  ];

  // Split images into two rows
  const row1Images = images.slice(0, 10);
  const row2Images = images.slice(10);

  // Lightbox handlers
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  // Touch scrolling handlers - Simplified approach
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStart(touch.clientX);
    setTouchMoved(false);
  };

  const handleTouchMove = (e) => {
    if (touchStart === null) return;
    
    const currentTouch = e.touches[0].clientX;
    const diff = Math.abs(touchStart - currentTouch);
    
    // If moved more than 10px horizontally, it's a swipe (prevent lightbox)
    if (diff > 10) {
      setTouchMoved(true);
      // Prevent default to stop page scrolling during horizontal swipe
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
    // Clear moved flag after a short delay
    setTimeout(() => setTouchMoved(false), 200);
  };

  const handleImageClick = (index, e) => {
    // Only open lightbox if it wasn't a swipe
    if (!touchMoved) {
      openLightbox(index);
    }
  };

  return (
    <>
      <div 
        className="scrolling-gallery" 
        ref={galleryRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Row 1 - Scrolls Right */}
        <div className="gallery-row gallery-row-1">
          <div className="gallery-track">
            {[...row1Images, ...row1Images, ...row1Images].map((img, index) => (
              <div 
                key={`row1-${index}`} 
                className="gallery-item"
                onClick={(e) => handleImageClick(index % row1Images.length, e)}
              >
                <img src={img} alt={`Project ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Row 2 - Scrolls Left */}
        <div className="gallery-row gallery-row-2">
          <div className="gallery-track">
            {[...row2Images, ...row2Images, ...row2Images].map((img, index) => (
              <div 
                key={`row2-${index}`} 
                className="gallery-item"
                onClick={(e) => handleImageClick(10 + (index % row2Images.length), e)}
              >
                <img src={img} alt={`Project ${index + 11}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              ×
            </button>
            <button className="lightbox-nav lightbox-prev" onClick={prevImage}>
              ‹
            </button>
            <img 
              src={images[currentImageIndex]} 
              alt={`Project ${currentImageIndex + 1}`}
              className="lightbox-image"
            />
            <button className="lightbox-nav lightbox-next" onClick={nextImage}>
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ScrollingGallery;
